import React, { createContext, useState, useContext, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  useEffect(() => {
    // On component mount, check for a token in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');

    if (urlToken) {
      // If a token is found in the URL, it's a new login.
      // Store it and update the state.
      localStorage.setItem('token', urlToken);
      setToken(urlToken);
      // Clean the URL by removing the token from the query string.
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []); // This effect runs only once on mount to capture the token from the URL.

  useEffect(() => {
    // This effect runs whenever the token state changes.
    if (token) {
      try {
        const decodedUser = jwtDecode(token);
        // Check if the token is expired
        if (decodedUser.exp * 1000 > Date.now()) {
          setUser(decodedUser);
        } else {
          // Token is expired, clear it.
          setUser(null);
          setToken(null);
          localStorage.removeItem('token');
        }
      } catch (error) {
        console.error('Invalid token:', error);
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
      }
    } else {
      setUser(null);
    }
  }, [token]); // This effect depends on the token state.

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    window.location.href = '/'; // Redirect to home page on logout
  };

  const value = {
    isAuthenticated: !!user,
    user,
    token,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
